import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import "../../../assets/css/gamesCss/Graph/otherCategories.css"
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";


const GraphTheoryGame = () => {
  const [stage, setStage] = useState(1);
  const [lives, setLives] = useState(3);
  const [showGameOver, setShowGameOver] = useState(false);
  const [showGameWon, setShowGameWon] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const { selLanguage, toggleLanguage, isLoading } = useLanguage();
  

  // Stage 1: DAG Selection
  const [selectedGraphResult, setSelectedGraphResult] = useState(null); // { id, isCorrect }


  // Stage 2: Strongly Connected Graph Builder
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const stage2Nodes = ["A", "B", "C", "D", "E"];

  const positions = {
                     A: { x: 100, y: 65 },
                   B: { x: 450, y: 80 },
                    C: { x: 250, y: 200 },
                     D: { x: 120, y: 340 }, 
                     E: { x: 550, y: 260 } };
  // Stage 3: Dijkstra Shortest Path
  const [selectedPath, setSelectedPath] = useState([]);
  const { darkMode } = useDarkMode();

  useEffect(() => {
    if (lives <= 0) {
      setShowGameOver(true);
    }
  }, [lives]);

  // Stage 1: DAG graphs (only one is correct)
  const dagGraphs = [
    {
      id: 1,
      name: selLanguage?"Γράφος 1":"Graph 1",
      isCorrect: true,
      nodes: [
        { id: "A", x: 150, y: 50 },
        { id: "B", x: 100, y: 150 },
        { id: "C", x: 200, y: 150 },
        { id: "D", x: 150, y: 250 }
      ],
      edges: [
        { from: "A", to: "B" },
        { from: "A", to: "C" },
        { from: "B", to: "D" },
        { from: "C", to: "D" }
      ]
    },
    {
      id: 2,
      name: selLanguage?"Γράφος 2":"Graph 2",
      isCorrect: false,
      nodes: [
        { id: "A", x: 150, y: 50 },
        { id: "B", x: 100, y: 150 },
        { id: "C", x: 200, y: 150 },
        { id: "D", x: 150, y: 250 }
      ],
      edges: [
        { from: "A", to: "B" },
        { from: "B", to: "C" },
        { from: "C", to: "A" }, // Cycle!
        { from: "B", to: "D" }
      ]
    },
    {
      id: 3,
      name: selLanguage?"Γράφος 3":"Graph 3",
      isCorrect: false,
      nodes: [
        { id: "A", x: 150, y: 50 },
        { id: "B", x: 100, y: 150 },
        { id: "C", x: 200, y: 150 },
        { id: "D", x: 150, y: 250 }
      ],
      edges: [
        { from: "A", to: "B" },
        { from: "A", to: "C" },
        { from: "B", to: "D" },
        { from: "D", to: "B" } // Cycle!
      ]
    },
    {
      id: 4,
      name: selLanguage?"Γράφος 4":"Graph 4",
      isCorrect: false,
      nodes: [
        { id: "A", x: 100, y: 100 },
        { id: "B", x: 200, y: 50 },
        { id: "C", x: 200, y: 150 },
        { id: "D", x: 100, y: 200 }
      ],
      edges: [
        { from: "A", to: "B" },
        { from: "B", to: "C" },
        { from: "C", to: "D" },
        { from: "D", to: "A" } // Full cycle!
      ]
    }
  ];

  // Stage 3: Weighted graph for shortest path
  const stage3Graph = {
    nodes: [
      { id: "S", label: "START", x: 100, y: 150 },
      { id: "A", x: 250, y: 80 },
      { id: "B", x: 250, y: 220 },
      { id: "C", x: 400, y: 80 },
      { id: "D", x: 400, y: 220 },
      { id: "E", label: "END", x: 550, y: 150 }
    ],
    edges: [
      { from: "S", to: "A", weight: 2 },
      { from: "S", to: "B", weight: 5 },
      { from: "A", to: "C", weight: 3 },
      { from: "A", to: "D", weight: 8 },
      { from: "B", to: "A", weight: 1 },
      { from: "B", to: "D", weight: 2 },
      { from: "C", to: "E", weight: 4 },
      { from: "D", to: "C", weight: 1 },
      { from: "D", to: "E", weight: 6 }
    ]
  };

  const handleGraphSelect = async (graphId) => {
    setSelectedGraphResult({ id: graphId, isCorrect: null }); // Αντί για setSelectedGraph(graphId)
    setIsChecking(true);
    setFeedback(null);
    await new Promise(resolve => setTimeout(resolve, 500));

    const graph = dagGraphs.find(g => g.id === graphId);
    if (graph.isCorrect) {
      setSelectedGraphResult({ id: graphId, isCorrect: true }); // Προσθήκη
      setFeedback({
        success: true,
        message: selLanguage?"Σωστά! Αυτός είναι ο μόνος DAG. Προχωράς στο επόμενο στάδιο!":"Correct! This is the only DAG. You advance to the next stage!"
      });
      setTimeout(() => {
        setStage(2);
        setFeedback(null);
        setSelectedGraphResult(null); // Αντί για setSelectedGraph(null)
      }, 2000);
    } else {
      setSelectedGraphResult({ id: graphId, isCorrect: false }); // Προσθήκη
      setFeedback({
        success: false,
        message: selLanguage?"Λάθος! Αυτός ο γράφος περιέχει κύκλο και δεν είναι DAG.":"Wrong! This graph contains a cycle and is not a DAG."
      });
      setLives(prev => prev - 1);
      // Προσθήκη: Reset μετά από 2 δευτερόλεπτα
      setTimeout(() => {
        setSelectedGraphResult(null);
      }, 2000);
    }
    setIsChecking(false);
  };

  const handleNodeClick = (nodeId) => {
    if (stage === 2) {
      if (!selectedNode) {
        setSelectedNode(nodeId);
      } else if (selectedNode === nodeId) {
        setSelectedNode(null);
      } else {
        const newEdge = { from: selectedNode, to: nodeId };
        const edgeExists = edges.some(e => e.from === selectedNode && e.to === nodeId);
        if (!edgeExists) {
          setEdges([...edges, newEdge]);
        }
        setSelectedNode(null);
      }
    } else if (stage === 3) {
      if (selectedPath.length === 0 || selectedPath[selectedPath.length - 1] !== nodeId) {
        const lastNode = selectedPath.length > 0 ? selectedPath[selectedPath.length - 1] : "S";
        const edgeExists = stage3Graph.edges.some(e => e.from === lastNode && e.to === nodeId);
        if (edgeExists || selectedPath.length === 0) {
          setSelectedPath([...selectedPath, nodeId]);
        }
      }
    }
  };

  const checkStronglyConnected = async () => {
    setIsChecking(true);
    setFeedback(null);
    await new Promise(resolve => setTimeout(resolve, 500));

    // Check if strongly connected: every node must reach every other node
    const canReach = (from, to, visited = new Set()) => {
      if (from === to) return true;
      visited.add(from);
      const outgoing = edges.filter(e => e.from === from && !visited.has(e.to));
      for (let edge of outgoing) {
        if (canReach(edge.to, to, visited)) return true;
      }
      return false;
    };

    let isStronglyConnected = true;
    for (let node1 of stage2Nodes) {
      for (let node2 of stage2Nodes) {
        if (node1 !== node2) {
          if (!canReach(node1, node2)) {
            isStronglyConnected = false;
            break;
          }
        }
      }
      if (!isStronglyConnected) break;
    }

    if (isStronglyConnected && edges.length > 0) {
      setFeedback({
        success: true,
        message: selLanguage?"Μπράβο! Δημιούργησες ένα ισχυρά συνεκτικό γράφο! Προχωράς στο τελευταίο στάδιο!":"Well done! You created a strongly connected graph! You advance to the final stage!"
      });
      setTimeout(() => {
        setStage(3);
        setFeedback(null);
      }, 2000);
    } else {
      setFeedback({
        success: false,
        message: selLanguage?"Λάθος! Ο γράφος δεν είναι ισχυρά συνεκτικός. Κάθε κόμβος πρέπει να φτάνει σε όλους τους άλλους!":"Wrong! The graph is not strongly connected. Every node must reach all other nodes!"
      });
      setLives(prev => prev - 1);
    }
    setIsChecking(false);
  };

  const checkShortestPath = async () => {
    setIsChecking(true);
    setFeedback(null);
    await new Promise(resolve => setTimeout(resolve, 500));

    if (selectedPath.length === 0) {
      setFeedback({
        success: false,
        message: selLanguage?"Επίλεξε ένα μονοπάτι!":"Select a path!"
      });
      setIsChecking(false);
      return;
    }

    const fullPath = ["S", ...selectedPath];


    let isValid = true;
    let totalWeight = 0;
    for (let i = 0; i < fullPath.length - 1; i++) {
      const edge = stage3Graph.edges.find(e => e.from === fullPath[i] && e.to === fullPath[i + 1]);
      if (!edge) {
        isValid = false;
        break;
      }
      totalWeight += edge.weight;
    }

    if (!isValid) {
      setFeedback({
        success: false,
        message: selLanguage?"Το μονοπάτι δεν είναι έγκυρο!":" The path is not valid!"
      });
      setLives(prev => prev - 1);
    } else if (fullPath[fullPath.length - 1] !== "E") {
      setFeedback({
        success: false,
        message: selLanguage?"Το μονοπάτι δεν φτάνει στο END!":" The path does not reach END!"
      });
      setLives(prev => prev - 1);
    } else {
      // Optimal path is S->B->D->C->E = 5+2+1+4 = 12
      const optimalWeight = 9;
      if (totalWeight === optimalWeight) {
        setShowGameWon(true);
      } else {
        setFeedback({
          success: false,
          message:  selLanguage?`Το μονοπάτι δεν είναι το συντομότερο! Βάρος: ${totalWeight} (Βέλτιστο: ${optimalWeight})`:`The path is not the shortest! Weight: ${totalWeight} (Optimal: ${optimalWeight})`
        });
        setLives(prev => prev - 1);
      }
    }
    setIsChecking(false);
  };

  const resetGame = () => {
    setStage(1);
    setLives(3);
    setShowGameOver(false);
    setShowGameWon(false);
    setSelectedGraphResult(null);
    setEdges([]);
    setSelectedNode(null);
    setSelectedPath([]);
    setFeedback(null);
  };

  const resetStage = () => {
    if (stage === 2) {
      setEdges([]);
      setSelectedNode(null);
    } else if (stage === 3) {
      setSelectedPath([]);
    }
    setFeedback(null);
  };

  const getLevelQuestion = (currentStage) => {
    if (currentStage === 1) {
      return selLanguage?"Επίλεξε τον γράφο που είναι Κατευθυνόμενος Ακυκλικός Γράφος (DAG)." :"Select the graph that is a Directed Acyclic Graph (DAG)."
    }
    else if (currentStage === 2) {
      return selLanguage? "Δημιούργησε έναν ισχυρά συνεκτικό κατευθυνόμενο γράφο προσθέτοντας ακμές μεταξύ των κόμβων." : "Create a strongly connected directed graph by adding edges between the nodes."
    }
    else {
      return selLanguage? "Επίλεξε το συντομότερο μονοπάτι από τον κόμβο START στον κόμβο END χρησιμοποιώντας τον αλγόριθμο του Dijkstra." : "Select the shortest path from the START node to the END node using Dijkstra's algorithm."
    }
  }

  return (
    <div
      className="cakeGameContainer main-content"
      style={{
        background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"
      }}
    >
      <GameOverModal isVisible={showGameOver} onPlayAgain={resetGame} />
      <GameWonModal isVisible={showGameWon} onPlayAgain={resetGame} />

      <div style={{ width: "100%", maxWidth: "1000px" }}>
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "30px"
        }}>
          <div style={{
            color: darkMode ? "white" : "#080808bb",
            padding: "12px 24px",
            borderRadius: "12px",
            fontWeight: "700",
            fontSize: "18px",
            border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #131212bb",
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}>
            {selLanguage?`Στάδιο ${stage}/3`:`Stage ${stage}/3`}
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            {[1, 2, 3].map((heartNum) => (
              <div key={heartNum} style={{
                fontSize: "28px",
                filter: lives >= heartNum ? "none" : "grayscale(100%) opacity(0.3)",
                transition: "all 0.3s ease"
              }}>
                ❤️
              </div>
            ))}
          </div>
        </div>

        <div className="cakeGamePrompt">

          {getLevelQuestion(stage)}
        </div>


        {/* Stage 1: Strongly Connected Graph Builder */}
        {stage === 1 && (
          <>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "20px"
            }}>
              {dagGraphs.map((graph) => (
                <motion.div
                  key={graph.id}
                  onClick={() => !isChecking && handleGraphSelect(graph.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    background: "white",
                    borderRadius: "16px",
                    padding: "20px",
                    cursor: isChecking ? "default" : "pointer",
                    border: selectedGraphResult?.id === graph.id
                      ? selectedGraphResult.isCorrect === true
                        ? "3px solid #10b981"  // Πράσινο αν σωστό
                        : selectedGraphResult.isCorrect === false
                          ? "3px solid #ef4444"  // Κόκκινο αν λάθος
                          : "3px solid #fbbf24"  // Κίτρινο κατά τον έλεγχο
                      : "3px solid transparent", boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease"
                  }}
                >
                  <h3 style={{ textAlign: "center", color: "#0e194bff", marginBottom: "15px" }}>
                    {graph.name}
                  </h3>
                  <svg width="100%" height="300" viewBox="0 0 300 300">


                    <defs>
                      <marker id={`arrow-${graph.id}`} markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                        <polygon points="0 0, 10 3, 0 6" fill="#2f4fdbff" />
                      </marker>

                      <linearGradient id="nodeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#1d4ed8" />
                      </linearGradient>

                    </defs>
                    {graph.edges.map((edge, idx) => {
                      const fromNode = graph.nodes.find(n => n.id === edge.from);
                      const toNode = graph.nodes.find(n => n.id === edge.to);

                      // Υπολογισμός για να σταματήσει η γραμμή πριν τον κύκλο
                      const dx = toNode.x - fromNode.x;
                      const dy = toNode.y - fromNode.y;
                      const distance = Math.sqrt(dx * dx + dy * dy);
                      const ratio = (distance - 25) / distance; // 25 = radius του κύκλου

                      return (
                        <line
                          key={idx}
                          x1={fromNode.x}
                          y1={fromNode.y}
                          x2={fromNode.x + dx * ratio}
                          y2={fromNode.y + dy * ratio}
                          stroke="#2f4fdbff"
                          strokeWidth="3"
                          markerEnd={`url(#arrow-${graph.id})`}
                        />
                      );
                    })}
                    {graph.nodes.map((node) => (
                      <g key={node.id}>
                        <circle
                          cx={node.x}
                          cy={node.y}
                          r="25"
                          fill="url(#nodeGradient)"
                          stroke="#667eea"
                          strokeWidth="2"
                        />
                        <text
                          x={node.x}
                          y={node.y + 6}
                          textAnchor="middle"
                          fill="white"
                          fontSize="18"
                          fontWeight="700"
                        >
                          {node.id}
                        </text>
                      </g>
                    ))}
                  </svg>
                </motion.div>
              ))}
            </div>
          </>
        )}


        
        {/* Stage 2: Strongly Connected Graph Builder */}
        {stage === 2 && (
          <>
            {selectedNode && (
              <p style={{
                color: "#f0f0f0ff",
                textAlign: "center",
                marginTop: "15px",
                fontWeight: "600",
                fontSize: "16px"
              }}>
                {selLanguage?`Επιλεγμένος κόμβος: ${selectedNode} - Κλικ σε άλλον κόμβο για δημιουργία ακμής`:`Selected Node: ${selectedNode} - Click another node to create an edge`}
              </p>
            )}
            <div style={{
              background: "white",
              borderRadius: "16px",
              padding: "30px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              marginBottom: "20px"
            }}>
              <svg width="100%" height="400" viewBox="0 0 600 400">
                <defs>
                  <marker id="arrow-stage2" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                    <polygon points="0 0, 10 3, 0 6" fill="#2f4fdbff" />
                  </marker>

                  <linearGradient id="nodeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#1d4ed8" />
                  </linearGradient>
                </defs>

                {edges.map((edge, idx) => {
                  const fromPos = positions[edge.from];
                  const toPos = positions[edge.to];

                  // Υπολογισμός για να σταματήσει η γραμμή πριν τον κύκλο
                  const dx = toPos.x - fromPos.x;
                  const dy = toPos.y - fromPos.y;
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  const ratio = (distance - 35) / distance; // 35 = radius του κύκλου

                  return (
                    <line
                      key={idx}
                      x1={fromPos.x}
                      y1={fromPos.y}
                      x2={fromPos.x + dx * ratio}
                      y2={fromPos.y + dy * ratio}
                      stroke="#2f4fdbff"
                      strokeWidth="3"
                      markerEnd="url(#arrow-stage2)"
                    />
                  );
                })}
                {stage2Nodes.map((node) => {
                  const pos = positions[node];
                  return (
                    <g key={node}>
                      <motion.circle
                        cx={pos.x}
                        cy={pos.y}
                        r="35"
                        fill={selectedNode === node ? "#050381ff" : "url(#nodeGradient)"}
                        stroke="#adadadff"
                        strokeWidth="3"
                        style={{ cursor: 'pointer' }}
                        whileHover={{ scale: 1.1 }}
                        onClick={() => handleNodeClick(node)}
                      />
                      <text
                        x={pos.x}
                        y={pos.y + 7}
                        textAnchor="middle"
                        fill="white"
                        fontSize="24"
                        fontWeight="700"
                        style={{ pointerEvents: 'none' }}
                      >
                        {node}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>

            <div style={{ textAlign: "center", display: "flex", gap: "15px", justifyContent: "center" }}>
              <button
                className="varietytDirectedCheckbtn"
                onClick={checkStronglyConnected}
                disabled={isChecking || edges.length === 0}
                style={{
                  cursor: isChecking || edges.length === 0 ? "not-allowed" : "pointer",
                  opacity: isChecking || edges.length === 0 ? 0.6 : 1,
                }}
              >
                {isChecking ? selLanguage?"Έλεγχος...":"Checking..." : 
                selLanguage?"Έλεγχος Γράφου":"Check Graph"}
              </button>
              <button
                onClick={resetStage}
                className="vairetyDirectedResetbtn"
              >
                {selLanguage?"Επανεκκίνηση":"Reset"}
              </button>
            </div>
          </>
        )}

        {/* Stage 3: Dijkstra Shortest Path */}
        {stage === 3 && (
          <>

            {selectedPath.length > 0 && (
              <p style={{
                color: "#e7e7e7ff",
                textAlign: "center",
                marginTop: "15px",
                fontWeight: "600",
                fontSize: "16px"
              }}>
                {selLanguage?`Μονοπάτι: START → ${selectedPath.join(" → ")}`:`Path: START → ${selectedPath.join(" → ")}`}
              </p>
            )}


            <div style={{
              background: "white",
              borderRadius: "16px",
              padding: "30px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              marginBottom: "20px"
            }}>
              <svg width="100%" height="300" viewBox="0 0 650 300">
                <defs>
                  <marker id="arrow-stage3" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                    <polygon points="0 0, 10 3, 0 6" fill="#2f4fdbff" />
                  </marker>

                  <linearGradient id="nodeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#1d4ed8" />
                  </linearGradient>
                </defs>

                {stage3Graph.edges.map((edge, idx) => {
                  const fromNode = stage3Graph.nodes.find(n => n.id === edge.from);
                  const toNode = stage3Graph.nodes.find(n => n.id === edge.to);

                  // Υπολογισμός για να σταματήσει η γραμμή πριν τον κύκλο
                  const dx = toNode.x - fromNode.x;
                  const dy = toNode.y - fromNode.y;
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  const ratio = (distance - 35) / distance; // 35 = radius του κύκλου

                  const midX = (fromNode.x + toNode.x) / 2;
                  const midY = (fromNode.y + toNode.y) / 2;

                  return (
                    <g key={idx}>
                      <line
                        x1={fromNode.x}
                        y1={fromNode.y}
                        x2={fromNode.x + dx * ratio}
                        y2={fromNode.y + dy * ratio}
                        stroke="#2f4fdbff"
                        strokeWidth="3"
                        markerEnd="url(#arrow-stage3)"
                      />
                      <circle cx={midX - 7} cy={midY} r="18" fill="#4e7dd4ff" />
                      <text
                        x={midX - 7}
                        y={midY + 8}
                        textAnchor="middle"
                        fill="#000000ff"
                        fontSize="16"
                        fontWeight="700"

                      >
                        {edge.weight}
                      </text>
                    </g>
                  );
                })}
                {stage3Graph.nodes.map((node) => {
                  const isInPath = selectedPath.includes(node.id);
                  const canSelect = selectedPath.length === 0
                    ? node.id !== "S"
                    : stage3Graph.edges.some(e =>
                      e.from === (selectedPath.length > 0 ? selectedPath[selectedPath.length - 1] : "S")
                      && e.to === node.id
                    );
                  return (
                    <g key={node.id}>
                      <motion.circle
                        cx={node.x}
                        cy={node.y}
                        r="35"
                        fill={isInPath ? "#050381ff" : "url(#nodeGradient)"}
                        stroke={canSelect ? "#adadadff" : "#667eea"}
                        strokeWidth={canSelect ? "4" : "2"}
                        style={{ cursor: canSelect ? 'pointer' : 'default' }}
                        whileHover={canSelect ? { scale: 1.1 } : {}}
                        onClick={() => canSelect && handleNodeClick(node.id)}
                      />
                      <text
                        x={node.x}
                        y={node.y + 7}
                        textAnchor="middle"
                        fill="white"
                        fontSize="18"
                        fontWeight="700"
                        style={{ pointerEvents: 'none' }}
                      >
                        {node.label || node.id}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>

            <div style={{ textAlign: "center", display: "flex", gap: "15px", justifyContent: "center" }}>
              <button
                onClick={checkShortestPath}
                disabled={isChecking || selectedPath.length === 0}
                className="varietytDirectedCheckbtn"
                style={{
                  cursor: isChecking || selectedPath.length === 0 ? "not-allowed" : "pointer",
                  opacity: isChecking || selectedPath.length === 0 ? 0.6 : 1,
                }}
              >
                {isChecking ? selLanguage?"Έλεγχος..." :"Checking..." :
                 selLanguage?"Έλεγχος Μονοπατιού":"Check Path"}
              </button>
              <button
                onClick={resetStage}
                className="vairetyDirectedResetbtn"
              >
                {selLanguage?"Επανεκκίνηση":"Reset"}
              </button>
            </div>
          </>
        )}

        {/* Feedback */}
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              textAlign: "center",
              padding: "20px",
              borderRadius: "16px",
              marginTop: "25px",
              background: feedback.success
                ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
              border: `3px solid ${feedback.success ? "#10b981" : "#ef4444"}`,
              color: feedback.success ? "#059669" : "#dc2626",
              fontWeight: 600,
              fontSize: "18px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)"
            }}
          >
            {feedback.message}
          </motion.div>
        )}
      </div>
    </div >
  );
};

export default GraphTheoryGame;